import { type Components, type Theme } from "@mui/material";

import { defaultFontFamily } from "../fontFamily.ts";

export type StyleOverrideProps = { theme: Omit<Theme, "components"> };

export const buttonTheme: Components<Theme>["MuiButton"] = {
  defaultProps: {
    disableElevation: true,
    color: "secondary",
    variant: "contained",
  },

  styleOverrides: {
    root: ({ theme }) => ({
      fontFamily: defaultFontFamily,
      borderRadius: theme.borderRadius.md,
      textTransform: "none",
      textDecoration: "none",
      padding: "8px 16px",
      whiteSpace: "nowrap",

      // For the circular progress inside button, default settings don't work
      // so we need to override the size for that case
      "& :where(svg:not(.MuiCircularProgress-svg))": {
        width: "20px",
        height: "20px",
      },
      "& svg[data-icon] *[stroke]": {
        vectorEffect: "non-scaling-stroke",
      },
      variants: [
        {
          props: { color: "errorSecondary", variant: "contained" },
          style: {
            backgroundColor: theme.palette.errorSecondary.main,
            color: theme.palette.errorSecondary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.errorSecondary.dark,
            },
          },
        },
        {
          props: { color: "emphasis", variant: "contained" },
          style: {
            backgroundColor: theme.palette.custom.fillEmphasis,
            color: theme.palette.text.main,
            "&:hover": {
              backgroundColor: theme.palette.custom.fillEmphasisHover,
            },
            "&:active": {
              backgroundColor: theme.palette.custom.fillEmphasisActive,
            },
          },
        },
      ],
    }),
    containedPrimary: ({ theme }) => ({
      "&:hover": {
        background: theme.palette.custom.fillHover,
      },
      "&:active": {
        background: theme.palette.custom.fillActive,
      },
    }),
    containedSecondary: ({ theme }) => ({
      backgroundColor: theme.palette.custom.fillSecondary,
      color: theme.palette.text.main,
      "&:hover": {
        background: theme.palette.custom.fillSecondaryHover,
      },
    }),
    containedSuccess: ({ theme }) => ({
      backgroundColor: theme.palette.success.main,
      "&:hover": {
        background: theme.palette.custom.fillInverse,
      },
    }),
    containedError: ({ theme }) => ({
      backgroundColor: theme.palette.error.main,
      "&:hover": {
        background: theme.palette.error.dark,
      },
    }),
    textPrimary: ({ theme }) => ({
      color: theme.palette.text.main,
      "&:hover": {
        background: theme.palette.custom.fillSecondaryHover,
      },
    }),
    textSecondary: ({ theme }) => ({
      color: theme.palette.text.secondary,
      "&:hover": {
        background: theme.palette.custom.fillSecondaryHover,
      },
    }),
    textError: ({ theme }) => ({
      color: theme.palette.errorSecondary.contrastText,
      "&:hover": {
        background: theme.palette.errorSecondary.dark,
      },
    }),
    containedSizeSmall: ({ theme }) => ({
      borderRadius: theme.borderRadius.sm,
      padding: "4px 12px",
    }),
    textSizeSmall: ({ theme }) => ({
      borderRadius: theme.borderRadius.sm,
      padding: "4px 12px",
    }),
    containedSizeLarge: ({ theme }) => ({
      borderRadius: theme.borderRadius.lg,
      padding: "12px 24px",
    }),
    textSizeLarge: ({ theme }) => ({
      borderRadius: theme.borderRadius.lg,
      padding: "12px 24px",
    }),
  },
};
